import React from 'react';
import { useNavigate } from 'react-router-dom';
import { loadTestScenario } from '../features/email-analyzer/utils/test-scenarios';

/**
 * Email Analyzer Test Page
 * 
 * This page provides an overview of the available test scenarios 
 * for the email content analyzer feature.
 */
const EmailAnalyzerTestPage: React.FC = () => {
  const navigate = useNavigate();
  
  const handleRunTest = (testName: string) => {
    // Load the test scenario
    const scenario = loadTestScenario(testName);
    if (!scenario) {
      console.error(`Test scenario "${testName}" not found`);
      return;
    }
    
    // Navigate to the analyzer page with test parameters in query string
    navigate(`/email-analyzer?test=${encodeURIComponent(testName)}`);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white shadow-sm rounded-lg p-6">
        <h1 className="text-3xl font-semibold text-[#111817] mb-6">Email Content Analyzer Test Suite</h1>
        
        <p className="text-[#3A4144] mb-6">
          This test suite helps verify the functionality of the email content analyzer by running predefined test scenarios.
          Each test focuses on different aspects of email analysis, from spam detection to formatting issues.
        </p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <button 
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors"
            onClick={() => handleRunTest("Spam Keyword Test")}>
            Run Spam Keyword Test
          </button>
          
          <button 
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors"
            onClick={() => handleRunTest("Suspicious Formatting Test")}>
            Run Formatting Test
          </button>
          
          <button 
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors"
            onClick={() => handleRunTest("Multiple Links Test")}>
            Run Links Test
          </button>
          
          <button 
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors"
            onClick={() => handleRunTest("Image Placeholders Test")}>
            Run Image Test
          </button>
          
          <button 
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors"
            onClick={() => handleRunTest("Dollar Sign Test")}>
            Run Dollar Sign Test
          </button>
          
          <button 
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors"
            onClick={() => handleRunTest("Empty Inputs Test")}>
            Run Empty Inputs Test
          </button>
          
          <button 
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors"
            onClick={() => handleRunTest("Clean Professional Email")}>
            Run Clean Email Test
          </button>
          
          <button 
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors"
            onClick={() => handleRunTest("Mixed Content Email")}>
            Run Mixed Content Test
          </button>
        </div>
        
        <section className="mb-8">
          <h2 className="text-2xl font-medium text-[#111817] mb-4">Available Tests</h2>
          
          <div className="space-y-4">
            <div>
              <h3 className="font-medium text-[#111817]">Spam Keyword Test</h3>
              <p className="text-[#111817]">Tests detection of spam trigger keywords with high spam scores.</p>
            </div>
            
            <div>
              <h3 className="font-medium text-[#111817]">Suspicious Formatting Test</h3>
              <p className="text-[#111817]">Verifies detection of suspicious formatting patterns like excessive capitalization.</p>
            </div>
            
            <div>
              <h3 className="font-medium text-[#111817]">Multiple Links Test</h3>
              <p className="text-[#111817]">Tests detection of multiple links and URL shorteners which can trigger spam filters.</p>
            </div>
            
            <div>
              <h3 className="font-medium text-[#111817]">Image Placeholders Test</h3>
              <p className="text-[#111817]">Verifies detection of excessive image usage which can affect deliverability.</p>
            </div>
            
            <div>
              <h3 className="font-medium text-[#111817]">Dollar Sign Test</h3>
              <p className="text-[#111817]">Tests the analyzer's ability to properly detect dollar sign patterns ($, $$$) and categorize financial content.</p>
            </div>
            
            <div>
              <h3 className="font-medium text-[#111817]">Empty Inputs Test</h3>
              <p className="text-[#111817]">Verifies that the analyzer properly validates inputs and prevents analysis of empty content.</p>
            </div>
            
            <div>
              <h3 className="font-medium text-[#111817]">Clean Professional Email</h3>
              <p className="text-[#111817]">Tests how the analyzer scores a well-crafted professional email without spam triggers.</p>
            </div>
            
            <div>
              <h3 className="font-medium text-[#111817]">Mixed Content Email</h3>
              <p className="text-[#111817]">Tests the analyzer with an email containing a mix of acceptable content and some potential triggers.</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default EmailAnalyzerTestPage; 